import { trackUserActivity } from '@/services/api/UserActivity';

class UserActivityTracker {
  constructor() {
    // Only enable tracking in production environment
    this.isEnabled = this.isProductionEnvironment();
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
    this.queue = [];
    this.processing = false;
    
    // Log environment status
    if (!this.isEnabled) {
      console.log('UserActivityTracker: Disabled in non-production environment');
    }
  }

  /**
   * Check if we're in production environment
   * @returns {boolean}
   */
  isProductionEnvironment() {
    // Check both ENV and NEXT_PUBLIC_ENV
    const env = process.env.ENV;
    const nextPublicEnv = process.env.NEXT_PUBLIC_ENV;
    
    return env === 'production' || nextPublicEnv === 'production';
  }

  /**
   * Tracks user activity asynchronously
   * @param {Object} activityData - Activity data to track
   * @param {string} activityData.full_uri - Complete URL where the activity occurred
   * @param {string} activityData.page_title - Title of the page where activity happened
   * @param {string} activityData.event_name - Name of the event (e.g., 'page_view', 'click', 'download')
   * @param {string} activityData.label - Descriptive label for the activity
   * @param {string} [activityData.email] - User's email address (optional)
   * @param {string} [activityData.ip_address] - User's IP address (optional)
   * @param {string} [activityData.resource_type] - Type of resource being accessed (optional)
   * @param {number} [activityData.resource_type_id] - ID of the specific resource (optional)
   */
  async track(activityData) {
    if (!this.isEnabled) return;

    // Double-check environment in case it changed
    if (!this.isProductionEnvironment()) {
      console.log('UserActivityTracker: Skipping tracking in non-production environment');
      return;
    }

    try {
      // Validate required fields
      const requiredFields = ['full_uri', 'page_title', 'event_name', 'label'];
      for (const field of requiredFields) {
        if (!activityData[field]) {
          console.warn(`UserActivityTracker: Missing required field '${field}'`);
          return;
        }
      }

      // Add to queue for processing
      this.queue.push({
        ...activityData,
        timestamp: new Date().toISOString(),
        attempts: 0
      });

      // Process queue if not already processing
      if (!this.processing) {
        this.processQueue();
      }
    } catch (error) {
      console.error('UserActivityTracker: Error queuing activity:', error);
    }
  }

  /**
   * Process the activity queue
   */
  async processQueue() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;

    while (this.queue.length > 0) {
      const activity = this.queue.shift();
      await this.sendActivity(activity);
    }

    this.processing = false;
  }

  /**
   * Send activity to the API
   * @param {Object} activity - Activity data
   */
  async sendActivity(activity) {
    try {
      const response = await trackUserActivity(activity, {
        timeout: 5000, // 5 second timeout
      });

      // Success - activity tracked
      if (response.status === 200 || response.status === 201) {
        console.log('UserActivityTracker: Activity tracked successfully', {
          event: activity.event_name,
          label: activity.label
        });
      }
    } catch (error) {
      // Handle errors gracefully without affecting the main app
      this.handleTrackingError(error, activity);
    }
  }

  /**
   * Handle tracking errors with retry logic
   * @param {Error} error - The error that occurred
   * @param {Object} activity - The activity that failed to track
   */
  handleTrackingError(error, activity) {
    activity.attempts = (activity.attempts || 0) + 1;

    if (activity.attempts < this.retryAttempts) {
      // Retry after delay
      setTimeout(() => {
        this.queue.unshift(activity); // Add back to front of queue
        if (!this.processing) {
          this.processQueue();
        }
      }, this.retryDelay * activity.attempts); // Exponential backoff

      console.warn(`UserActivityTracker: Retrying activity tracking (attempt ${activity.attempts}/${this.retryAttempts})`, {
        event: activity.event_name,
        error: error.message
      });
    } else {
      // Max retries reached - log error but don't crash the app
      console.error('UserActivityTracker: Failed to track activity after maximum retries', {
        event: activity.event_name,
        label: activity.label,
        error: error.message,
        response: error.response?.data
      });
    }
  }

  /**
   * Track page view activity
   * @param {Object} pageData - Page view data
   */
  trackPageView(pageData) {
    const { url, title, user, ipAddress } = pageData;
    
    this.track({
      full_uri: url,
      page_title: title,
      event_name: 'page_view',
      label: `User viewed page: ${title}`,
      email: user?.email || null,
      ip_address: ipAddress || null,
      resource_type: 'page',
      resource_type_id: null
    });
  }

  /**
   * Track content interaction (reports, articles, etc.)
   * @param {Object} contentData - Content interaction data
   */
  trackContentInteraction(contentData) {
    const { contentType, contentId, contentTitle, action, user, ipAddress } = contentData;
    
    this.track({
      full_uri: window.location.href,
      page_title: document.title,
      event_name: `${contentType}_${action}`,
      label: `User ${action} ${contentType}: ${contentTitle}`,
      email: user?.email || null,
      ip_address: ipAddress || null,
      resource_type: contentType,
      resource_type_id: contentId
    });
  }

  /**
   * Track user interactions (clicks, downloads, etc.)
   * @param {Object} interactionData - Interaction data
   */
  trackInteraction(interactionData) {
    const { element, action, label, user, ipAddress, additionalData = {} } = interactionData;
    
    this.track({
      full_uri: window.location.href,
      page_title: document.title,
      event_name: action,
      label: label || `User ${action} on ${element}`,
      email: user?.email || null,
      ip_address: ipAddress || null,
      ...additionalData
    });
  }

  /**
   * Disable tracking
   */
  disable() {
    this.isEnabled = false;
    console.log('UserActivityTracker: Tracking disabled');
  }

  /**
   * Enable tracking
   */
  enable() {
    this.isEnabled = true;
    console.log('UserActivityTracker: Tracking enabled');
  }

  /**
   * Force enable tracking (for testing in non-production)
   * Use with caution - this overrides environment checks
   */
  forceEnable() {
    this.isEnabled = true;
    console.warn('UserActivityTracker: Force enabled - ignoring environment checks');
  }

  /**
   * Get current queue size
   */
  getQueueSize() {
    return this.queue.length;
  }

  /**
   * Clear the queue
   */
  clearQueue() {
    this.queue = [];
    console.log('UserActivityTracker: Queue cleared');
  }
}

// Create singleton instance
const userActivityTracker = new UserActivityTracker();

export default userActivityTracker;